"use client";

import classNames from "classnames";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { useSelector } from "react-redux";

import BrandDropdown from "@stadium/shared/src/components/header/BrandDropdown";
import Multimedia from "@stadium/shared/src/components/shared/multimedia";
import LinkWithLang from "@components/swagmagic/dynamic/slices/client-link-with-lang";
import HeaderActions from "@components/swagmagic/header-actions";
import HeaderMobileNavMenu from "@components/swagmagic/header-mobile-nav-menu";
import HeaderNav from "@components/swagmagic/header-nav";
import HeaderMenu from "@components/swagmagic/header-menu";
import { slideOutPanel } from "@components/swagmagic/header-mobile-nav-menu/slide-panel";

import { SwagmagicStickyHeader } from "@components/swagmagic/header/swagmagic-sticky-header";

import {
  NavCategory,
  HeaderProps,
  HeaderDataProps,
  LogoData,
  PoweredByData,
  CategoryConfig,
} from "@components/swagmagic/header/type";

import { getIsMyDesignedSwagPresent } from "@selectors/menu";

import {
  CATEGORY_PRODUCT_LISTING_LINK_TYPE,
  HEADER_LOGO_LINK_TYPE,
  LinkWithLangType,
  OTHER_LINK_TYPE,
} from "@stadium/shared/src/constants/stores/routes";

import { getDynamicCatalogNav, isActiveNavLink } from "@helpers/swagmagic/header";

import { useStoreContext } from "@components/store/provider";

import { MY_DESIGNED_SWAG_LOCKER } from "@constants/menu";

import defaultLogoImage from "@stadium/shared/src/assets/images/plasmic-static-slices/header-logo.svg";
import stadiumLogo from "@stadium/shared/src/assets/images/stadium-logo.svg";

import styles from "./main.module.scss";

const hasValidDestination = (item: { code?: string; href?: string } | null | undefined): boolean =>
  Boolean(item?.code || item?.href?.trim());

const filterValidCategories = (categories: NavCategory[] | undefined): NavCategory[] => {
  if (!Array.isArray(categories) || categories.length === 0) return [];

  return categories.filter(hasValidDestination).map((category) => ({
    ...category,
    links: category.links?.filter(hasValidDestination) ?? [],
    quickLinks: category.quickLinks?.filter(hasValidDestination) ?? [],
  }));
};

const DEFAULT_OBJ = {};
const DEFAULT_ARRAY = [];

const Header = ({ headerData, dynamicNavData, profileText = "Profile" }: HeaderProps) => {
  const {
    headerCta,
    headerNav,
    logo,
    poweredByImage: poweredByImageData,
    categoriesWithNoHomePage,
  } = headerData ?? (DEFAULT_OBJ as HeaderDataProps);

  const { showMenuHeader } = useStoreContext();
  const pathname = usePathname() || "";

  const dynamicCatalogNav = getDynamicCatalogNav({
    categories: dynamicNavData?.categories,
    categoriesWithNoHomePage,
  });

  const { image: logoImage, alt: logoAlt, link: logoLink } = logo ?? (DEFAULT_OBJ as LogoData);
  const logoLinkValue = logoLink || "/";
  const { image: poweredByImage, alt: poweredByAlt } =
    poweredByImageData || (DEFAULT_OBJ as PoweredByData);

  const [menuPopup, setMenuPopup] = useState(false);
  const [hoveredNav, setHoveredNav] = useState<string | null>(null);
  const [displayedNav, setDisplayedNav] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const overlayRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const scrollYRef = useRef(0);
  const wasMenuOpenRef = useRef(false);

  const handleMenuClose = () => {
    slideOutPanel({
      panelRef: menuRef,
      overlayRef,
      onComplete: () => setMenuPopup(false),
    });
  };

  const isMyDesignedSwagPresent = useSelector(getIsMyDesignedSwagPresent);

  const validHeaderNav = [
    ...(isMyDesignedSwagPresent
      ? [
          {
            navOption: {
              id: "my-designed-swag",
              title: "My Designed Swag",
              linkType: CATEGORY_PRODUCT_LISTING_LINK_TYPE as LinkWithLangType,
              code: MY_DESIGNED_SWAG_LOCKER,
            } as CategoryConfig,
          },
        ]
      : []),
    dynamicCatalogNav,
    ...(headerNav || DEFAULT_ARRAY),
  ]
    ?.filter(Boolean)
    ?.map((nav) => {
      const navOption = nav.navOption || (DEFAULT_OBJ as CategoryConfig);
      return {
        ...navOption,
        categories: filterValidCategories(navOption?.categories || []),
      };
    });

  const handleMouseEnter = (nav) => {
    if (nav?.categories?.length > 0) {
      setHoveredNav(nav.title);
    }
  };

  const handleMouseLeave = (nav) => {
    if (nav?.categories?.length > 0) {
      setHoveredNav(null);
    }
  };

  useEffect(() => {
    const body = document.body;

    if (menuPopup) {
      scrollYRef.current = window.scrollY;
      wasMenuOpenRef.current = true;
      body.style.overflow = "hidden";
      body.style.position = "fixed";
      body.style.top = `-${scrollYRef.current}px`;
      body.style.width = "100%";
    } else {
      body.style.overflow = "";
      body.style.position = "";
      body.style.top = "";
      body.style.width = "";
      if (wasMenuOpenRef.current) {
        window.scrollTo(0, scrollYRef.current);
        wasMenuOpenRef.current = false;
      }
    }

    return () => {
      body.style.overflow = "";
      body.style.position = "";
      body.style.top = "";
      body.style.width = "";
    };
  }, [menuPopup]);

  useEffect(() => {
    if (hoveredNav) {
      setDisplayedNav(hoveredNav);
      requestAnimationFrame(() => {
        setIsVisible(true);
      });
    } else {
      setIsVisible(false);
      const timer = setTimeout(() => {
        setDisplayedNav(null);
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [hoveredNav]);

  return (
    <>
      <SwagmagicStickyHeader>
        <header
          className={classNames(styles.header, {
            [styles.myDesignedSwagPresent]: isMyDesignedSwagPresent,
          })}
        >
          <div className={styles.plasmicContainer}>
            <div className={styles.leftSection}>
              <LinkWithLang
                type={logoLinkValue === "/" ? HEADER_LOGO_LINK_TYPE : OTHER_LINK_TYPE}
                data={{ href: logoLinkValue }}
                className={styles.logoLink}
              >
                <Multimedia
                  type="image"
                  src={logoImage?.url || defaultLogoImage}
                  alt={logoAlt}
                  className={styles.logoImage}
                />
              </LinkWithLang>
              <div className={styles.divider}></div>
              <BrandDropdown renderChildrenAsToggleButton dropDownClassName={styles.brandDropdown}>
                <div className={styles.poweredByContainer}>
                  <div className={styles.poweredByWrapper}>
                    <p className={styles.poweredByText}>POWERED BY</p>
                    <Multimedia
                      type="image"
                      src={poweredByImage?.url ?? stadiumLogo}
                      alt={poweredByAlt}
                      className={styles.poweredByImage}
                    />
                  </div>
                </div>
              </BrandDropdown>
            </div>
            <div className={styles.rightSection}>
              <ul className={styles.nav}>
                {validHeaderNav?.map((nav, index) => {
                  const hasDropdown = nav?.categories?.length > 0;
                  const isDropdownOpen = Boolean(hasDropdown && displayedNav === nav.title);
                  return (
                    <li
                      key={nav.id || nav.title || index}
                      onMouseEnter={() => handleMouseEnter(nav)}
                      onMouseLeave={() => handleMouseLeave(nav)}
                      className={classNames(styles.navItem, {
                        [styles.hasDropdown]: hasDropdown,
                        [styles.dropdownOpenNavItem]: isDropdownOpen,
                      })}
                    >
                      {nav.linkType ? (
                        <LinkWithLang
                          type={nav.linkType}
                          data={nav}
                          className={styles.navItemTitle}
                          activeClassName={(linkUrl) =>
                            isActiveNavLink(pathname, linkUrl) ? styles.activeNavItem : ""
                          }
                          target={nav.openInNewTab ? "_blank" : "_self"}
                          showExternalLinkIcon={nav.openInNewTab}
                        >
                          {nav.title}
                        </LinkWithLang>
                      ) : (
                        <span className={styles.navItemTitle}>{nav.title}</span>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className={styles.dividerAlt}></div>
              <HeaderActions
                headerCta={headerCta}
                setMenuPopup={setMenuPopup}
                menuPopup={menuPopup}
              />
            </div>
          </div>
          {menuPopup && (
            <HeaderMobileNavMenu
              profileText={profileText}
              ctaButtonData={headerCta}
              validHeaderNav={validHeaderNav}
              overlayRef={overlayRef}
              menuRef={menuRef}
              handleClose={handleMenuClose}
            />
          )}
          {displayedNav && (
            <nav
              className={`${styles.dropdown} ${
                isVisible ? styles.dropdownVisible : styles.dropdownHidden
              }`}
              onMouseEnter={() => {
                if (displayedNav) {
                  setHoveredNav(displayedNav);
                }
              }}
              onMouseLeave={() => setHoveredNav(null)}
              role="navigation"
              aria-label="Dropdown navigation"
            >
              {validHeaderNav?.map((nav, index) => (
                <HeaderNav
                  key={nav.id || nav.title || index}
                  categories={nav.categories}
                  onLinkClick={() => setHoveredNav(null)}
                  className={classNames({
                    [styles.sectionVisible]: displayedNav === nav.title,
                  })}
                />
              ))}
            </nav>
          )}
        </header>
      </SwagmagicStickyHeader>
      {showMenuHeader && <HeaderMenu />}
    </>
  );
};

export default Header;
